import * as THREE from 'three';

function dimensionKey(values) {
  return values.map((value) => Number(value).toFixed(3)).join(':');
}

export class GeometryFactory {
  #cache = new Map();

  box(width, height, depth) {
    return this.#get(`box:${dimensionKey([width, height, depth])}`, () => new THREE.BoxGeometry(width, height, depth));
  }

  cylinder(radiusTop, radiusBottom, height, segments = 12) {
    return this.#get(`cyl:${dimensionKey([radiusTop, radiusBottom, height, segments])}`, () => new THREE.CylinderGeometry(radiusTop, radiusBottom, height, segments));
  }

  sphere(radius, widthSegments = 16, heightSegments = 10, phiLength = Math.PI * 2, thetaLength = Math.PI) {
    return this.#get(`sphere:${dimensionKey([radius, widthSegments, heightSegments, phiLength, thetaLength])}`, () => new THREE.SphereGeometry(radius, widthSegments, heightSegments, 0, phiLength, 0, thetaLength));
  }

  cone(radius, height, segments = 4) {
    return this.#get(`cone:${dimensionKey([radius, height, segments])}`, () => new THREE.ConeGeometry(radius, height, segments));
  }

  hipRoof(width, depth, height) {
    return this.#get(`hip:${dimensionKey([width, depth, height])}`, () => {
      const halfWidth = width / 2;
      const halfDepth = depth / 2;
      const ridgeAlongX = width >= depth;
      const ridge = Math.max(0.1, Math.abs(width - depth) * 0.48);
      const positions = ridgeAlongX ? [
        -halfWidth, 0, -halfDepth, halfWidth, 0, -halfDepth, halfWidth, 0, halfDepth, -halfWidth, 0, halfDepth,
        -ridge / 2, height, 0, ridge / 2, height, 0
      ] : [
        -halfWidth, 0, -halfDepth, halfWidth, 0, -halfDepth, halfWidth, 0, halfDepth, -halfWidth, 0, halfDepth,
        0, height, -ridge / 2, 0, height, ridge / 2
      ];
      const indices = ridgeAlongX ? [
        0, 1, 5, 0, 5, 4,
        1, 2, 5,
        2, 3, 4, 2, 4, 5,
        3, 0, 4,
        0, 3, 2, 0, 2, 1
      ] : [
        0, 1, 4,
        1, 2, 5, 1, 5, 4,
        2, 3, 5,
        3, 0, 4, 3, 4, 5,
        0, 3, 2, 0, 2, 1
      ];
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
      const uvs = [];
      for (let index = 0; index < positions.length; index += 3) {
        uvs.push((positions[index] + halfWidth) / Math.max(width, 0.001));
        uvs.push((positions[index + 2] + halfDepth) / Math.max(depth, 0.001));
      }
      geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
      geometry.setIndex(indices);
      geometry.computeVertexNormals();
      geometry.computeBoundingBox();
      geometry.computeBoundingSphere();
      return geometry;
    });
  }

  #get(key, create) {
    if (!this.#cache.has(key)) this.#cache.set(key, create());
    return this.#cache.get(key);
  }

  dispose() {
    for (const geometry of this.#cache.values()) geometry.dispose();
    this.#cache.clear();
  }
}
